import React, { useState, useRef, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { t, Trans } from "@lingui/macro";
import { useHistory } from "react-router-dom";
import shallow from "zustand/shallow";

import { generateId, getS3FileKey } from "base/utils";
import { getUploadBaseUrl } from "base/form";
import { getStore } from "base/basestore";
import { useUserStore, useAccountStore } from "base/stores";
import { SignaturePopup } from "widgets/signature";
import { LazyLoadComponent } from "widgets/lazyloader";
import { usePickerDetailsStore, Button, BUTTON_TYPES } from "widgets/inputs";
import { FileUpload } from "widgets/fileUpload";
import { Icon } from "widgets/datadisplay";
import { Alert } from "widgets/feedback";
import { i18n } from "base/i18n";

import { useSignStore } from "./signature.store";
import { Base } from "../base";
import { Close as CloseIcon } from "@/icons";

import styles from "./signature.css";

import PlusMediumIcon from "./PlusMedium.svg";

export function Signature(props) {
  const { column, value, pos, row, instanceId } = props;

  const baseRef = useRef(null);
  const [currentValue, setCurrentValue] = useState(value);
  // const [isEditing, setIsEditing] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const signatureFile = useRef(null);
  const userDetails = useUserStore.getState().currentUser || {};
  const { currentAppId } = useUserStore(
    (state) => ({
      currentAppId: state.currentAppId
    }),
    shallow
  );
  let [signatureFilePicker, setSignatureFilePicker] = useState(false);
  const [uploadedData, setUploadedData] = useState(null);
  const [clearValue, setClear] = useState(false);
  const [flowData, setFlowData] = useState({});
  const [imageData, setImageData] = useState(null);
  const [allowDraw, setAllowDraw] = useState(true);
  const accountDetails = useAccountStore.getState();
  const updateSignatureData = useSignStore.getState().updateSignatureData;
  const history = useHistory();
  useEffect(
    function getColumnFields() {
      if (column.actionPath) {
        usePickerDetailsStore.setState({ actionPath: column.actionPath });
      }
      if (column && column.hasOwnProperty("AllowDraw")) {
        setAllowDraw(column["AllowDraw"]);
      }
    },
    [column]
  );

  useEffect(
    function onValueChange() {
      setCurrentValue(value || null);
    },
    [value, row, pos]
  );

  const posTop = pos && pos.top;
  const columnId = column.id;

  useEffect(
    function updateEditing() {
      baseRef.current.stopEditing();
      // setIsEditing(false);
    },
    [posTop, columnId]
  );

  useEffect(
    function setFlowDataFromInstanceId() {
      if (instanceId) {
        // eslint-disable-next-line no-unused-vars
        const useFormStore = getStore(instanceId);
        const flowData = useFormStore.getState().flowData;
        setFlowData(flowData);
      }
    },
    [instanceId]
  );

  const enableUpload = useMemo(
    function updateEnableUpload() {
      if (column && column.hasOwnProperty("enableUpload")) {
        return column.enableUpload;
      }
      return true;
    },
    [column]
  );

  const setMySignature = function () {
    let sigData = {
      name: column.id,
      key: userDetails.Signature && getS3FileKey(userDetails.Signature.key)
    };
    setCurrentValue(sigData.key ? sigData : null);
    baseRef.current.dataChanged(sigData.key ? sigData : null);
    if (!sigData.key) {
      Alert.showConfirm({
        title: i18n._(t`Update signature`),
        content: (
          <Trans>
            You cannot draw a signature. Add a signature in your&nbsp;
            <b>About me</b> section to use it here.
          </Trans>
        ),
        okText: i18n._(t`View profile`),
        onOK: () => {
          if (currentAppId) {
            history.push(`/account/mysettings/myprofile`);
          } else {
            history.push(`/account/user/profile/${userDetails._id}`);
          }
        },
        cancelText: i18n._(t`Cancel`),
        onCancel: () => {}
      });
      return;
    }
    baseRef.current.dataCompleted(false);
  };
  return (
    <Base
      {...props}
      ref={baseRef}
      type="signature"
      onEditing={() => {}}
      onUnload={() => {
        // setIsEditing(false);
        baseRef.current.dataCompleted();
      }}
    >
      {currentValue && (
        <SignatureItem
          {...props}
          isPublicForm={flowData.isPublicForm}
          clearValue={() => {
            setClear(true);
            setUploadedData(null);
            setCurrentValue(null);
            baseRef.current.dataChanged(null);
            baseRef.current.dataCompleted(false);
          }}
          height="8rem"
        />
      )}
      {!currentValue && (
        <div className={styles.signature}>
          {!currentValue &&
            !column.readOnly &&
            (allowDraw || flowData.isPublicForm) && (
              <button
                className={styles.add}
                onClick={() => {
                  baseRef.current.startEditing();
                  setOpenDialog(true);
                }}
              >
                <PlusMediumIcon />
              </button>
            )}
          {!currentValue &&
            !allowDraw &&
            !flowData.isPublicForm &&
            !column.readOnly && (
              <div className={styles.useSignature}>
                <Button
                  type={BUTTON_TYPES.TERTIARY}
                  onClick={setMySignature}
                  className={styles.addSignButton}
                >
                  <Trans>Use My Signature</Trans>
                </Button>
              </div>
            )}

          {openDialog && (
            <SignaturePopup
              readOnly={false}
              uploadSignature={(blob, imageData) => {
                setClear(false);
                signatureFile.current = new File([blob], `${column.id}.png`);
                signatureFile.current.id = generateId("sig");
                if (flowData.isPublicForm) {
                  setImageData(imageData);
                }
              }}
              useMySignature={setMySignature}
              enableAttachment={enableUpload}
              clearValue={clearValue}
              clearSignature={() => {
                setClear(true);
                setUploadedData(null);
                signatureFile.current = null;
                baseRef.current.dataChanged(null);
              }}
              closePopup={() => {
                setOpenDialog(false);
              }}
              onSave={() => {
                if (!uploadedData && signatureFile.current) {
                  let fileUpload = new FileUpload(
                    signatureFile.current,
                    `${column.prefixUrl}/${row.id}`,
                    {
                      isSignature: true
                    }
                  );
                  setIsSaving(true);
                  fileUpload.upload(getUploadBaseUrl(), (err, data) => {
                    const value = {
                      key: getS3FileKey(data?.key),
                      name: column.id
                    };
                    setCurrentValue(value);
                    setIsSaving(false);
                    if (flowData.isPublicForm) {
                      updateSignatureData(row.id, column.id, imageData);
                    }
                    setOpenDialog(false);
                    baseRef.current.dataChanged(value);
                    baseRef.current.dataCompleted(false);
                  });
                } else {
                  setOpenDialog(false);
                  baseRef.current.dataChanged(uploadedData);
                  baseRef.current.dataCompleted(false);
                  setUploadedData(null);
                }
              }}
              showLoading={isSaving}
              disableUseSignature={!userDetails.Signature}
              handleAttachment={() => setSignatureFilePicker(true)}
              uploadType={"attach"}
              value={currentValue}
              uploadedData={uploadedData}
              isPublicForm={flowData.isPublicForm}
            />
          )}
          <LazyLoadComponent
            component={() => import("kfplatform/filePicker")}
            componentName="FilePicker"
            open={signatureFilePicker}
            onUploadDone={(value) => {
              if (value) {
                let Signature = {
                  key: getS3FileKey(value[0].key),
                  name: value[0].name
                };
                setUploadedData(Signature);
                // handleInput("Signature", Signature);
                setSignatureFilePicker(false);
              }
            }}
            prefix={userDetails._id}
            onPickerClose={() => {
              setSignatureFilePicker(false);
            }}
            options={{
              fileExtensions: ["JPG", "JPEG", "JPE", "PNG"],
              maxSize: 10485760,
              maxCount: 1,
              isSignature: true
            }}
            baseURL={`/upload/2/${accountDetails._id}/`}
          />
        </div>
      )}
    </Base>
  );
}

function SignatureItem(props) {
  const {
    value,
    height = "100%",
    column,
    row,
    readOnly,
    isPublicForm,
    clearValue
  } = props;
  const publicFormDataUrl = useSignStore((state) =>
    state.getSignatureData(row.id, column.id)
  );

  const getStyle = useMemo(
    function getStyle() {
      let url = isPublicForm
        ? publicFormDataUrl
        : value && `/upload/2/${getS3FileKey(value.key)}`;

      return url
        ? {
            backgroundImage: `url(${url})`,
            height: height
          }
        : {};
    },
    [isPublicForm, publicFormDataUrl, value, height]
  );
  return (
    <div className={styles.signatureItem} style={getStyle}>
      {!readOnly && (
        <Icon
          onClick={clearValue}
          className={`${styles.clearIcon} shadow-6dp`}
          paddingNone={true}
        >
          <CloseIcon />
        </Icon>
      )}
    </div>
  );
}

SignatureItem.propTypes = {
  column: PropTypes.any,
  clearValue: PropTypes.func,
  value: PropTypes.any,
  height: PropTypes.string,
  row: PropTypes.any,
  readOnly: PropTypes.bool,
  isPublicForm: PropTypes.bool
};

Signature.propTypes = {
  column: PropTypes.any,
  show: PropTypes.bool,
  value: PropTypes.any,
  pos: PropTypes.any,
  row: PropTypes.any,
  instanceId: PropTypes.string
};
